/* Copyright (c) 2020 Pej AB. All rights reserved.
 * Created by Marcus Johansson on 2019-08-26. */

import firebase from 'firebase/app';
import {combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {PejFirestoreService} from '../core/firestore';
import {Item, MenuOptionGroup, MenuOptionItem} from '../models';
import {FirestoreMap} from '../models/shared/firestore-map';
import {firestoreQueryListener} from '../utils/firestore';
import {mapObject} from '../utils/object-mapper';
import {shareCached} from '../utils/rx';
import {trackingArrayEquals} from '../utils/tracking';
import {PejItemFirestoreService} from './item-firestore';
import Query = firebase.firestore.Query;

function fsMenuOptionGroups(shopId: number): Query {
    return PejFirestoreService.firestore().collection('shops').doc(`${shopId}`).collection('menu-option-groups');
}

export class PejMenuOptionFirestoreServiceClass {
    private observableCache: { [id: string]: Observable<any> } = {};

    public forShop(shopId: number): Observable<FirestoreMap<MenuOptionGroup>> {
        const cachePath = `shops/${shopId}/menu-option-groups`;
        return shareCached(this.observableCache, cachePath, () =>
             firestoreQueryListener(fsMenuOptionGroups(shopId), MenuOptionGroup)
                .pipe(map((it: FirestoreMap<MenuOptionGroup>) => {
                    for (const obj of it.values()) {
                        obj.shopId = shopId;
                    }
                    return it;
                })));
    }

    public forShopOnline(shopId: number): Observable<FirestoreMap<MenuOptionGroup>> {
        return this.forShop(shopId).pipe(filter(m => !m?.fromCache));
    }

    public forShopWithStatus(shopId: number): Observable<FirestoreMap<MenuOptionGroup>> {
        const cachePath = `shops/${shopId}/menu-option-groups|withStatus`;
        return shareCached(this.observableCache, cachePath, () =>
            combineLatest([PejItemFirestoreService.forShop(shopId), this.forShop(shopId)])
                .pipe(map(([itemMap, forShopMap]: [FirestoreMap<Item>, FirestoreMap<MenuOptionGroup>]) => {
                    const result = new FirestoreMap<MenuOptionGroup>();
                    for (const [id, menuOptionGroup] of forShopMap.entries()) {
                        if (menuOptionGroup.list?.find(menuOptionItem => menuOptionItem.itemId != null)) {
                            // do not mutate original objects
                            const updatedMenuOptionGroup = mapObject(menuOptionGroup, MenuOptionGroup);
                            updatedMenuOptionGroup.list = menuOptionGroup.list.map(menuOptionItem => {
                                if (menuOptionItem.itemId != null) {
                                    const updatedMenuOptionItem = mapObject(menuOptionItem, MenuOptionItem);
                                    const itemFromItemId = itemMap.get('' + menuOptionItem.itemId);
                                    updatedMenuOptionItem.status = (itemFromItemId != null) ? itemFromItemId.status : 'hidden';
                                    return updatedMenuOptionItem;
                                } else {
                                    return menuOptionItem;
                                }
                            });
                            result.set(id, updatedMenuOptionGroup);
                        } else {
                            result.set(id, menuOptionGroup);
                        }
                    }
                    result.fromCache = forShopMap.fromCache;
                    return result;
                })));
    }

    public forShopWithStatusOnline(shopId: number): Observable<FirestoreMap<MenuOptionGroup>> {
        return this.forShopWithStatus(shopId).pipe(filter(m => !m?.fromCache));
    }

    public groups(shopId: number, ids: string[]): Observable<MenuOptionGroup[]> {
        return this.forShopWithStatus(shopId).pipe(
            map((m: FirestoreMap<MenuOptionGroup>) =>
                ids.map(id => m.get(id)).filter(group => group != null)),
            distinctUntilChanged((prev, curr) => trackingArrayEquals(prev, curr))
        );
    }

    public groupsOnline(shopId: number, ids: string[]): Observable<MenuOptionGroup[]> {
        return this.forShopWithStatusOnline(shopId).pipe(
            map(m => ids.map(id => m.get(id)).filter(group => group != null)),
            distinctUntilChanged((prev, curr) => trackingArrayEquals(prev, curr))
        );
    }
}


export const PejMenuOptionFirestoreService = Object.seal(new PejMenuOptionFirestoreServiceClass());
/** @deprecated Use PejMenuOptionFirestoreService */
export const PejMenuOptionService = PejMenuOptionFirestoreService;
